import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { RodeoEventRun } from '../model/rodeoEventRun';
import { RodeoEvent } from '../model/rodeoEvent';
import { RodeoEventSchedule } from '../model/rodeoEventSchedule';
import { Rodeo } from '../model/rodeo';

@Injectable()
export class RunService {
    private deleted: number[] = [];
    private added: number[] = [];
    
    constructor(private api: ApiService) {

    }

    async updateLoadOrder(runs: RodeoEventRun[]) {
        return this.api.postWithAuth(this.api.endpoints.runs + '/UpdateLoadOrder', runs);
    }
    
    addTempDeleted(id: number) {
        this.deleted.push(id);

        setTimeout(() => {
            this.deleted = this.deleted.filter(x => x != id);
        }, 3000);
    }

    hasDeleted(id: number) {
        return this.deleted.find(x => x == id);
    }

    addTempAdded(id: number) {
        this.added.push(id);

        setTimeout(() => {
            this.added = this.added.filter(x => x != id);
        }, 3000);
    }

    hasAdded(id: number) {
        return this.added.find(x => x == id);
    }

    createRun(run: RodeoEventRun) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/create', run)
                .then(data => resolve(data), error => reject(error));
        });
    }

    deleteRun(runId) {
        return new Promise((resolve, reject) => {
            this.api
                .deleteWithAuth(this.api.endpoints.runs + '/delete?runId=' + runId)
                .then(data => resolve(data), error => reject(error));
        });
    }

    async getRun(runId) {
        const url = this.api.getWithAuth<RodeoEventRun>(this.api.endpoints.runs + '/get?runId=' + runId);
        return url;
    }

    updateRuns(runs: RodeoEventRun[]) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/update', runs)
                .then(data => resolve(data), error => reject(error));
        });
    }

    addSplitRunData(runs: RodeoEventRun[]) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/updateSplitData', runs)
                .then(data => resolve(data), error => reject(error));
        });
    }

    updateRunInfo(runs: RodeoEventRun[]) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/updateRunInfo', runs)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getUpdatedEventRuns(rodeoId: number, currentRuns) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/getUpdatedRuns?rodeoId=' + rodeoId, currentRuns)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getCompletedEventRuns(rodeoId: number, currentRuns) {
        return new Promise((resolve, reject) => {
            this.api
                .postWithAuth(this.api.endpoints.runs + '/getCompletedRuns?rodeoId=' + rodeoId, currentRuns)
                .then(data => resolve(data), error => reject(error));
        });
    }

    getRuns(rodeo: Rodeo, rodeoEvent: RodeoEvent, schedule: RodeoEventSchedule, round: number) {
        let runs = [];
    
        if (schedule.scheduleGroupId && schedule.scheduleGroupId != 0 && round <= rodeoEvent.rounds) {
            // Get all runs for the current schedule (prioritized)
            let scheduleRuns = schedule.runs;

            // Get all runs from schedules with the same scheduleGroupId
            let groupRuns = rodeo.rodeoEvents
                .flatMap(x => x.schedules)
                .filter(x => x.scheduleGroupId == schedule.scheduleGroupId)
                .flatMap(x => x.runs);

            // Extract unique numbers from the current schedule
            let existingRunNumbers = new Set(scheduleRuns.map(run => run.number));

            // Add the first run of each missing number from other schedules
            let additionalRuns = groupRuns
                .filter(run => !existingRunNumbers.has(run.number)) // Only missing numbers

            // Combine schedule runs with additional runs and sort by number
            runs = [...scheduleRuns, ...additionalRuns].sort((a, b) => a.number - b.number);
        } else {
            runs = schedule.runs;
        }

        if (round != 0) {
            runs = runs.filter(x => x.round == round);
        }

        if (runs.length > 0) {
            runs = this.sortRuns(rodeoEvent, runs, round);

            runs.forEach(run => {
                (run as any).showAsterisk = run.rodeoEventScheduleId !== schedule.id;
            });
        }
        
        return runs;
    }

    private sortRuns(rodeoEvent: RodeoEvent, runs: RodeoEventRun[], round: number) {
        if (runs.length == 0) return runs; 

        if (round > rodeoEvent.rounds) {
            runs.sort((a, b) => b.number - a.number);
        } else if (round == 2) {
            this.sortRound2Runs(rodeoEvent, runs);
        } else {
            runs.sort((a, b) => a.number - b.number);
        }

        return runs;
    }

    private sortRound2Runs(rodeoEvent: RodeoEvent, runs: RodeoEventRun[]) {
        if (rodeoEvent.reverseSecondRoundDragOnly) {
            runs.sort((a, b) => {
                if (Math.floor((a.number - 1) / rodeoEvent.dragEvery) < Math.floor((b.number - 1) / rodeoEvent.dragEvery))
                    return -1;
                else if (Math.floor((a.number - 1) / rodeoEvent.dragEvery) > Math.floor((b.number - 1) / rodeoEvent.dragEvery))
                    return 1;

                if (a.number % rodeoEvent.dragEvery == 0 && b.number % rodeoEvent.dragEvery != 0)
                    return -1;
                else if (a.number % rodeoEvent.dragEvery != 0 && b.number % rodeoEvent.dragEvery == 0)
                    return 1;

                if (a.number % rodeoEvent.dragEvery > b.number % rodeoEvent.dragEvery)
                    return -1;
                else if (a.number % rodeoEvent.dragEvery < b.number % rodeoEvent.dragEvery)
                    return 1;
                else
                    return 0;
            });
        } else if (rodeoEvent.reverseSecondRound) {
            runs.sort((a, b) => b.number - a.number);
        } else {
            runs.sort((a, b) => a.number - b.number);
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/run.service.ts